import { isRestrictedViewer } from "../../app/permissions";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { Alert, Button, DataTable, Field, Pagination, Dialog } from "../../ui";
import type { DataColumn } from "../../ui";
import { Panel, StateNotice } from "../workspace/shared";
import { money } from "../workspace/model";
import { saveBlob, failure } from "../workspace/behavior";

type Row = {
  managementNo: string;
  partyType: string;
  identityMasked: string;
  paymentMonth: string;
  approvalCount: number;
  cancellationCount: number;
  paymentCount: number;
  approvalAmount: number;
  cancellationAmount: number;
  amount: number;
};
type Report = {
  rows: Row[];
  missingPartyCount: number;
  missingPartyAmount: number;
  txtAvailable: boolean;
  missingTransactions?: { transactionId: number }[];
  txtUnavailableReason: string;
};
const columns: DataColumn<Row>[] = [
  { key: "month", title: "결제연월", render: (r) => r.paymentMonth },
  {
    key: "party",
    title: "납부 대상",
    render: (r) => (r.partyType === "BUSINESS" ? "법인" : "개인"),
  },
  { key: "identity", title: "식별번호", render: (r) => r.identityMasked },
  { key: "management", title: "관리번호", render: (r) => r.managementNo },
  {
    key: "approved",
    title: "승인 건수",
    numeric: true,
    render: (r) => r.approvalCount,
  },
  {
    key: "canceled",
    title: "취소 건수",
    numeric: true,
    render: (r) => r.cancellationCount,
  },
  {
    key: "count",
    title: "결제대행 건수",
    numeric: true,
    render: (r) => r.paymentCount,
  },
  {
    key: "paid",
    title: "승인 금액",
    numeric: true,
    render: (r) => money(r.approvalAmount),
  },
  {
    key: "refunded",
    title: "취소 금액",
    numeric: true,
    render: (r) => money(r.cancellationAmount),
  },
  {
    key: "amount",
    title: "결제 금액",
    numeric: true,
    render: (r) => money(r.amount),
  },
];
export function PaymentAgencyPage() {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  const readOnly = isRestrictedViewer(session.authSession?.authorities ?? []);
  const [params, setParams] = useSearchParams();
  const month = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
  }).format(new Date());
  const from = params.get("from") || month,
    to = params.get("to") || month;
  const [start, setStart] = useState(from),
    [end, setEnd] = useState(to);
  const [missingPage, setMissingPage] = useState(0);
  const [page, setPage] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const query = useQuery({
    queryKey: ["admin", "payment-agency", from, to],
    queryFn: async ({ signal }) =>
      (
        await apiClient.get<Report>("/api/v1/admin/payment-agency", {
          query: { from, to },
          signal,
          cache: "no-store",
        })
      ).data,
  });
  const report = query.data;
  const [textOpen, setTextOpen] = useState(false);
  const [officeCode, setOfficeCode] = useState("");
  const [businessName, setBusinessName] = useState("주식회사 티에프솔루션");
  const [missingId, setMissingId] = useState<number>();
  const [partyType, setPartyType] = useState("INDIVIDUAL");
  const [partyNumber, setPartyNumber] = useState("");
  const [reason, setReason] = useState("");
  const canComplete = !readOnly &&
    session.authSession?.authorities?.includes("TRANSACTION_REVIEW") &&
    session.authSession?.authorities?.includes("MEMBER_SENSITIVE_READ");
  const completeParty = async () => {
    if (!missingId || busy || !canComplete) return;
    setBusy(true);
    setError("");
    try {
      await apiClient.post(
        `/api/v1/admin/payment-agency/transactions/${missingId}/party`,
        { type: partyType, number: partyNumber, reason },
      );
      setMissingId(undefined);
      setPartyNumber("");
      setReason("");
      setMissingPage(0);
      await query.refetch();
    } catch (cause) {
      setError(failure(cause).message);
    } finally {
      setBusy(false);
    }
  };
  const canExport = !readOnly && session.authSession?.authorities?.includes(
    "MEMBER_SENSITIVE_READ",
  );
  const download = async (format: "xlsx" | "txt.zip" = "xlsx") => {
    setBusy(true);
    setError("");
    try {
      const response = await apiClient.get<Blob>(
        `/api/v1/admin/payment-agency/export.${format}`,
        {
          query: {
            from,
            to,
            ...(format === "txt.zip" ? { officeCode, businessName } : {}),
          },
          responseType: "blob",
          cache: "no-store",
        },
      );
      saveBlob(
        response.data,
        `payment-agency-${from}-${to}.${format === "txt.zip" ? "zip" : "xlsx"}`,
      );
      setTextOpen(false);
    } catch (cause) {
      setError(failure(cause).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-admin-page-id="ADM-S41" tabIndex={-1}>
      <Panel title="조회 조건">
        <form
          className="admin-filter-bar"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(0);
            setMissingPage(0);
            setParams({ from: start, to: end });
          }}
        >
          <Field
            compact
            required
            type="month"
            label="시작월"
            value={start}
            max={end}
            onChange={(e) => setStart(e.currentTarget.value)}
          />
          <Field
            compact
            required
            type="month"
            label="종료월"
            value={end}
            min={start}
            onChange={(e) => setEnd(e.currentTarget.value)}
          />
          <Button size="small" type="submit" isLoading={query.isFetching}>
            조회
          </Button>
        </form>
      </Panel>
      <Dialog
        open={textOpen}
        onClose={() => {
          if (!busy) setTextOpen(false);
        }}
        closeDisabled={busy}
        size="small"
        title="TEXT 제출 파일 만들기"
        primaryAction={
          <Button
            size="small"
            disabled={
              busy || !/^[0-9]{3}$/.test(officeCode) || !businessName.trim()
            }
            isLoading={busy}
            onClick={() => {
              void download("txt.zip");
            }}
          >
            다운로드
          </Button>
        }
        secondaryAction={
          <Button
            size="small"
            variant="quiet"
            disabled={busy}
            onClick={() => setTextOpen(false)}
          >
            취소
          </Button>
        }
      >
        <p>
          조회 기간의 자료를 분기별 TEXT 파일로 나누어 ZIP으로 다운로드합니다.
          관할 세무서 코드는 사업자등록증 기준으로 입력해 주세요.
        </p>
        <Field
          label="관할 세무서 코드"
          required
          inputMode="numeric"
          maxLength={3}
          value={officeCode}
          onChange={(e) => setOfficeCode(e.currentTarget.value)}
        />
        <Field
          label="제출자 상호"
          required
          value={businessName}
          onChange={(e) => setBusinessName(e.currentTarget.value)}
        />
        {error ? (
          <Alert persistent variant="danger" title="파일 생성 실패">
            {error}
          </Alert>
        ) : null}
      </Dialog>
      <Dialog
        open={missingId !== undefined}
        onClose={() => {
          if (!busy) {
            setMissingId(undefined);
            setPartyNumber("");
          }
        }}
        closeDisabled={busy}
        size="small"
        title="납부 대상 정보 보완"
        primaryAction={
          <Button
            size="small"
            disabled={
              !canComplete ||
              busy ||
              reason.trim().length < 5 ||
              !(partyType === "BUSINESS" ? /^[0-9]{10}$/ : /^[0-9]{13}$/).test(
                partyNumber,
              )
            }
            isLoading={busy}
            onClick={() => {
              void completeParty();
            }}
          >
            저장
          </Button>
        }
        secondaryAction={
          <Button
            size="small"
            variant="quiet"
            disabled={busy}
            onClick={() => {
              setMissingId(undefined);
              setPartyNumber("");
            }}
          >
            취소
          </Button>
        }
      >
        <p>
          거래 {missingId}의 실제 납부 대상을 확인하여 입력해 주세요. 결제한
          회원 정보로 자동 대체하지 않습니다.
        </p>
        <Field
          control="select"
          label="납부 대상"
          value={partyType}
          onChange={(e) => {
            setPartyType(e.currentTarget.value);
            setPartyNumber("");
          }}
        >
          <option value="INDIVIDUAL">개인</option>
          <option value="BUSINESS">법인</option>
        </Field>
        <Field
          label={partyType === "BUSINESS" ? "사업자등록번호" : "주민등록번호"}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={partyNumber}
          maxLength={partyType === "BUSINESS" ? 10 : 13}
          onChange={(e) =>
            setPartyNumber(e.currentTarget.value.replace(/[^0-9]/g, ""))
          }
          required
        />
        <Field
          label="보완 사유"
          control="textarea"
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.currentTarget.value)}
          required
        />
        {error ? (
          <Alert persistent variant="danger" title="저장 실패">
            {error}
          </Alert>
        ) : null}
      </Dialog>
      {error ? (
        <Alert variant="danger" title="처리 실패" persistent>
          {error}
        </Alert>
      ) : null}
      {query.isPending ? (
        <StateNotice state="loading" />
      ) : query.isError ? (
        <StateNotice
          state="error"
          onRefresh={() => {
            void query.refetch();
          }}
        />
      ) : report ? (
        <>
          {report.missingPartyCount > 0 ? (
            <Alert
              variant="warning"
              title="납부 대상 정보 확인 필요"
              persistent
            >
              식별정보 미등록 {report.missingPartyCount}건 · 승인/취소 절댓값
              합계 {money(report.missingPartyAmount)}. 누락된 거래가 있어
              다운로드할 수 없습니다.
            </Alert>
          ) : null}
          {report.missingTransactions?.length ? (
            <Panel title="납부 대상 미등록 거래">
              <DataTable
                columns={[
                  {
                    key: "id",
                    title: "거래ID",
                    render: (row: { transactionId: number }) => (
                      readOnly ? row.transactionId : <a href={`/transactions/${row.transactionId}`}>
                        {row.transactionId}
                      </a>
                    ),
                  },
                  {
                    key: "complete",
                    title: "정보 보완",
                    render: (row: { transactionId: number }) => (
                      <Button
                        size="small"
                        variant="secondary"
                        disabled={!canComplete || busy}
                        onClick={() => {
                          setMissingId(row.transactionId);
                          setPartyNumber("");
                          setReason("");
                          setError("");
                        }}
                      >
                        납부 대상 등록
                      </Button>
                    ),
                  },
                ]}
                rows={report.missingTransactions.slice(
                  missingPage * 20,
                  (missingPage + 1) * 20,
                )}
                rowKey={(row) => row.transactionId}
                caption="납부 대상 정보 미등록 거래"
              />
              <Pagination
                page={missingPage}
                size={20}
                totalCount={report.missingTransactions.length}
                onPageChange={setMissingPage}
              />
            </Panel>
          ) : null}
          <Panel
            title="결제대행자료"
            description="신용카드 승인·취소 완료일 기준입니다. 승인과 취소는 각각 1건으로 집계합니다."
          >
            <div className="admin-inline-actions">
              <Button
                size="small"
                variant="secondary"
                disabled={
                  !canExport || report.missingPartyCount > 0 || query.isFetching
                }
                isLoading={busy}
                onClick={() => {
                  void download();
                }}
              >
                엑셀 다운로드
              </Button>
              <Button
                size="small"
                variant="secondary"
                disabled={
                  !canExport ||
                  !report.txtAvailable ||
                  report.missingPartyCount > 0 ||
                  !report.rows.length ||
                  query.isFetching ||
                  busy
                }
                onClick={() => setTextOpen(true)}
              >
                TEXT 다운로드
              </Button>
            </div>
            {!canExport ? (
              <p>{readOnly ? "조회권한은 화면 조회만 가능합니다." : "민감정보 열람 권한이 있어야 다운로드할 수 있습니다."}</p>
            ) : null}
            <p>{report.txtUnavailableReason}</p>
            <DataTable
              columns={columns}
              rows={report.rows.slice(page * 20, (page + 1) * 20)}
              rowKey={(r) => `${r.managementNo}-${r.paymentMonth}`}
              caption="납부 대상별 결제대행자료"
            />
            <Pagination
              page={page}
              size={20}
              totalCount={report.rows.length}
              onPageChange={setPage}
            />
          </Panel>
        </>
      ) : null}
    </div>
  );
}
